import React, { useState } from 'react';
import { createAdvertisingInterest } from '../../services/advertisingService';
import { motion } from 'framer-motion';
import {
  MonitorIcon, TrendingUpIcon, MailIcon, HandshakeIcon,
  CheckIcon, UsersIcon, EyeIcon, MapPinIcon,
  TargetIcon, ZapIcon, BarChartIcon, QuoteIcon, XIcon,
} from 'lucide-react';
import PhoneInput from 'react-phone-number-input/input';

import AdvertisingBanner from '../../assets/img/advertising-banner.png';
import { isValidEmail } from '../../helper';

const BRAND = '#842014';
const BRAND_DARK = '#6b1a10';
const ORANGE = '#f9a720';
const SLATE_900 = '#0f172a';
const SLATE_800 = '#1e293b';

/* ─── Ad Interest Dialog ─── */
const INTEREST_OPTIONS = [
  'Display Ads',
  'Sponsored Listings',
  'Newsletter',
  'Custom Partnerships',
  'Other',
];

const BUDGET_OPTIONS = [
  '$0 – $500',
  '$500 – $2,000',
  '$2,000 – $5,000',
  '$5,000 – $10,000',
  '$10,000+',
];

const EMPTY_FORM = { name: '', email: '', phone: '', interest: '', budget: '' };

function AdDialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!isValidEmail(form.email)) errs.email = 'Email is not valid';
    if (form.phone) {
      if (!/^\+[1-9]\d{7,14}$/.test(form.phone)) errs.phone = 'Invalid phone number';
      else if (form.phone.replace(/\D/g, '').length !== 11) errs.phone = 'Phone number must be exactly 10 digits';
    }
    if (!form.interest) errs.interest = 'Please select an interest';
    if (!form.budget) errs.budget = 'Please select a budget';
    return errs;
  };

  const set = field => e => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handlePhoneChange = val => {
    setForm(prev => ({ ...prev, phone: val || '' }));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: '' }));
  };

  const handleClose = () => {
    onClose();
    setForm(EMPTY_FORM);
    setErrors({});
    setSubmitError('');
    setSuccess(false);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setLoading(true);
    setSubmitError('');
    const FIELD_MAP = { phoneNumber: 'phone', advertisingInterest: 'interest', advertisingBudget: 'budget' };
    try {
      await createAdvertisingInterest({
        name: form.name,
        email: form.email,
        phoneNumber: form.phone,
        advertisingInterest: form.interest,
        advertisingBudget: form.budget,
      });
      setSuccess(true);
      setForm(EMPTY_FORM);
    } catch (err) {
      const validationErrors = err?.response?.data?.error?.errors;
      if (validationErrors?.length) {
        const fieldErrs = {};
        validationErrors.forEach(({ fieldName, message }) => {
          const key = FIELD_MAP[fieldName] ?? fieldName;
          fieldErrs[key] = message;
        });
        setErrors(prev => ({ ...prev, ...fieldErrs }));
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackdrop = e => {
    if (e.target === e.currentTarget) handleClose();
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: '0.9rem',
    border: '1px solid #cbd5e1', outline: 'none', boxSizing: 'border-box',
    fontFamily: 'inherit', color: SLATE_900, background: '#fff',
  };
  const errorBorder = { border: '1px solid #f87171' };
  const errorText = { fontSize: '0.75rem', color: '#ef4444', marginTop: 4, display: 'block' };

  const labelStyle = {
    display: 'block', fontSize: '0.8125rem', fontWeight: 600,
    color: '#334155', marginBottom: 6,
  };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.22 }}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          background: BRAND, padding: '20px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h2 style={{ color: '#fff', fontWeight: 700, fontSize: '1.125rem', margin: 0 }}>
            Advertising Interest
          </h2>
          <button
            onClick={handleClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex' }}
          >
            <XIcon size={20} style={{ color: 'rgba(255,255,255,0.8)' }} />
          </button>
        </div>

        {/* Success state */}
        {success && (
          <div style={{ padding: '32px 24px', textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, background: '#dcfce7', borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px',
            }}>
              <CheckIcon size={28} style={{ color: '#16a34a' }} />
            </div>
            <h3 style={{ color: SLATE_900, fontWeight: 700, fontSize: '1.125rem', marginBottom: 8 }}>
              Thank you for your interest!
            </h3>
            <p style={{ color: '#64748b', fontSize: '0.9rem', marginBottom: 24 }}>
              We've received your submission and will be in touch shortly.
            </p>
            <button
              onClick={handleClose}
              style={{
                padding: '10px 28px', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem',
                border: 'none', background: BRAND, color: '#fff', cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        )}

        {/* Form */}
        {!success && <form onSubmit={handleSubmit} style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={labelStyle}>Name <span style={{ color: BRAND }}>*</span></label>
            <input
              type="text"
              placeholder="Your full name"
              value={form.name}
              onChange={set('name')}
              style={{ ...inputStyle, ...(errors.name ? errorBorder : {}) }}
            />
            {errors.name && <span style={errorText}>{errors.name}</span>}
          </div>

          <div>
            <label style={labelStyle}>Email <span style={{ color: BRAND }}>*</span></label>
            <input
              type="text"
              placeholder="you@company.com"
              value={form.email}
              onChange={set('email')}
              style={{ ...inputStyle, ...(errors.email ? errorBorder : {}) }}
            />
            {errors.email && <span style={errorText}>{errors.email}</span>}
          </div>

          <div>
            <label style={labelStyle}>Phone Number</label>
            <PhoneInput
              country="US"
              placeholder="+1 (555) 000-0000"
              value={form.phone}
              onChange={handlePhoneChange}
              maxLength={16}
              style={{ ...inputStyle, ...(errors.phone ? errorBorder : {}) }}
            />
            {errors.phone && <span style={errorText}>{errors.phone}</span>}
          </div>

          <div>
            <label style={labelStyle}>Advertising Interest <span style={{ color: BRAND }}>*</span></label>
            <select
              value={form.interest}
              onChange={set('interest')}
              style={{ ...inputStyle, appearance: 'auto', color: form.interest ? SLATE_900 : '#000409', ...(errors.interest ? errorBorder : {}) }}
            >
              <option value="" disabled>Select an option</option>
              {INTEREST_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.interest && <span style={errorText}>{errors.interest}</span>}
          </div>

          <div>
            <label style={labelStyle}>Advertising Budget <span style={{ color: BRAND }}>*</span></label>
            <select
              value={form.budget}
              onChange={set('budget')}
              style={{ ...inputStyle, appearance: 'auto', color: form.budget ? SLATE_900 : '#000409', ...(errors.budget ? errorBorder : {}) }}
            >
              <option value="" disabled>Select a range</option>
              {BUDGET_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
            {errors.budget && <span style={errorText}>{errors.budget}</span>}
          </div>

          {submitError && (
            <span style={{ fontSize: '0.8rem', color: '#ef4444', textAlign: 'center' }}>{submitError}</span>
          )}

          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem',
                border: '1px solid #cbd5e1', background: '#fff', color: '#475569', cursor: loading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = '#f1f5f9'; }}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                flex: 1, padding: '11px 0', borderRadius: 8, fontWeight: 600, fontSize: '0.9rem',
                border: 'none', background: loading ? '#b45a50' : BRAND, color: '#fff', cursor: loading ? 'not-allowed' : 'pointer',
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = BRAND_DARK; }}
              onMouseLeave={e => { if (!loading) e.currentTarget.style.background = BRAND; }}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>}
      </motion.div>
    </div>
  );
}

/* ─── Hero ─── */
function HeroSection({ onOpen }) {
  return (
    <section style={{
      position: 'relative',
      minHeight: 380,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url(${AdvertisingBanner})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.80)' }} />
      </div>

      <div style={{ position: 'relative', zIndex: 10, maxWidth: 900, margin: '0 auto', padding: '48px 24px', textAlign: 'center', color: '#fff' }}>
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)', color: '#e2e8f0', fontWeight: 700, marginBottom: 16, lineHeight: 1.2 }}
        >
          Reach Families Where They're Already Engaged
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          style={{ fontSize: '1.0625rem', color: '#e2e8f0', marginBottom: 32, maxWidth: 580, margin: '0 auto 28px' }}
        >
          Connect with 100,000+ active parents searching for kids activities, camps, and sports programs
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}
        >
          <button
            onClick={onOpen}
            style={{
              background: BRAND, color: '#fff', border: 'none', borderRadius: 8,
              padding: '12px 28px', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.background = BRAND}
          >
            Start Advertising
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Stats Bar ─── */
const statsData = [
  { icon: UsersIcon,      value: '100K+', label: 'Active Parents' },
  { icon: EyeIcon,        value: '1M+',   label: 'Monthly Page Views' },
  { icon: TrendingUpIcon, value: '85%',   label: 'Parent Decision Makers' },
  { icon: MapPinIcon,     value: '150+',  label: 'Cities Nationwide' },
];

function StatsBar() {
  return (
    <section style={{ background: SLATE_800, padding: '32px 0' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 24 }}>
          {statsData.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{ textAlign: 'center' }}
            >
              <stat.icon size={24} style={{ color: BRAND, margin: '0 auto 8px', display: 'block' }} />
              <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2rem)', fontWeight: 700, color: '#fff', marginBottom: 2 }}>
                {stat.value}
              </div>
              <div style={{ color: '#cbd5e1', fontSize: '0.875rem' }}>{stat.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Why Advertise ─── */
const benefits = [
  { icon: TargetIcon,   title: 'Targeted Reach',       description: 'Connect with parents actively searching for kids activities in your area. Your ads appear when you are needed most.' },
  { icon: ZapIcon,      title: 'High Intent Audience',  description: 'Reach decision-makers with purchasing power. Parents on CampsEZ are invested in their children and ready to buy quality products.' },
  { icon: BarChartIcon, title: 'Measurable Results',    description: 'Track every impression, click, and conversion. Our analytics dashboard shows exactly how your campaigns perform in real-time.' },
];

function WhyAdvertise() {
  return (
    <section style={{ padding: '56px 0', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 40 }}
        >
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, color: SLATE_900, marginBottom: 12 }}>
            Why Advertise With CampsEZ
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.9375rem', maxWidth: 560, margin: '0 auto' }}>
            Join hundreds of brands reaching engaged families at the perfect moment
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24 }}>
          {benefits.map((b, i) => (
            <motion.div
              key={b.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{ background: '#f8fafc', borderRadius: 12, padding: 24 }}
            >
              <div style={{
                width: 48, height: 48, background: BRAND, borderRadius: 10,
                display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16,
              }}>
                <b.icon size={22} style={{ color: '#fff' }} />
              </div>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: SLATE_900, marginBottom: 8 }}>
                {b.title}
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.65, margin: 0 }}>
                {b.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Ad Products ─── */
const products = [
  { icon: MonitorIcon,    title: 'Display Ads',          description: 'Eye-catching banner ads across activity listings, search results, and high-traffic pages.' },
  { icon: TrendingUpIcon, title: 'Sponsored Listings',   description: 'Boost your products to the top of relevant category pages where parents are actively browsing.' },
  { icon: MailIcon,       title: 'Newsletter Features',  description: 'Reach 50,000+ subscribers in our weekly parent newsletter with exclusive placement.' },
  { icon: HandshakeIcon,  title: 'Custom Partnerships',  description: 'Co-branded content, event sponsorships, and exclusive promotions with our team.' },
];

function AdProducts({ onOpen }) {
  return (
    <section style={{ padding: '56px 0', background: '#f8fafc' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 40 }}
        >
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, color: SLATE_900, marginBottom: 12 }}>
            Advertising Solutions for Every Goal
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.9375rem', maxWidth: 560, margin: '0 auto' }}>
            From display ads to custom partnerships, we have the right format for your brand
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {products.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{
                background: '#fff', borderRadius: 12, padding: 24,
                border: '1px solid #e2e8f0',
                transition: 'box-shadow 0.2s, border-color 0.2s',
              }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.10)'; e.currentTarget.style.borderColor = BRAND; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = '#e2e8f0'; }}
            >
              <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                <div style={{
                  width: 40, height: 40, background: ORANGE, borderRadius: 8, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <p.icon size={18} style={{ color: BRAND }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '1rem', fontWeight: 700, color: SLATE_900, marginBottom: 6 }}>
                    {p.title}
                  </h3>
                  <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.65, marginBottom: 10 }}>
                    {p.description}
                  </p>
                  <button
                    onClick={onOpen}
                    style={{
                      background: 'none', border: 'none', padding: 0,
                      color: BRAND, fontWeight: 600, fontSize: '0.875rem',
                      cursor: 'pointer', fontFamily: 'inherit',
                    }}
                    onMouseEnter={e => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={e => e.currentTarget.style.textDecoration = 'none'}
                  >
                    Learn More →
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── How It Works ─── */
const steps = [
  { number: '1', title: 'Choose Your Ad Format',       description: 'Select from display ads, sponsored listings, newsletter features, or custom partnerships.' },
  { number: '2', title: 'Set Your Budget & Audience',  description: 'Define your budget and target specific demographics, locations, and activity categories.' },
  { number: '3', title: 'Launch & Track Results',      description: 'Go live and monitor performance with real-time analytics.' },
];

function HowItWorks() {
  return (
    <section style={{ padding: '56px 0', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 40 }}
        >
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, color: SLATE_900, marginBottom: 12 }}>
            How It Works
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.9375rem', maxWidth: 560, margin: '0 auto' }}>
            Get started in three simple steps
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, position: 'relative' }}>
          {steps.map((step, i) => (
            <motion.div
              key={step.number}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}
            >
              <div style={{
                width: 80, height: 80, background: BRAND, borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 16, position: 'relative', zIndex: 1,
              }}>
                <span style={{
                  fontSize: '1.875rem', fontWeight: 700, color: '#fff',
                  fontFamily: "Georgia, 'Times New Roman', serif",
                }}>
                  {step.number}
                </span>
              </div>
              <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: SLATE_900, marginBottom: 8 }}>
                {step.title}
              </h3>
              <p style={{ color: '#64748b', fontSize: '0.875rem', lineHeight: 1.65, margin: 0 }}>
                {step.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Audience Insights ─── */
const insights = [
  'Parents aged 28-45 with active families',
  'Actively searching for kids activities and programs',
  'High purchase intent for family products and services',
  'Engaged community seeking trusted recommendations',
];

const demographics = [
  { label: 'Parents 28-45',    value: 68 },
  { label: 'Decision Makers',  value: 85 },
  { label: 'Mobile Users',     value: 73 },
];

function AudienceInsights() {
  return (
    <section style={{ padding: '56px 0', background: '#f8fafc' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 40, alignItems: 'center' }}>

          {/* Left: text + bullets */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, color: SLATE_900, marginBottom: 16 }}>
              Know Your Audience
            </h2>
            <p style={{ color: '#64748b', fontSize: '0.9375rem', lineHeight: 1.7, marginBottom: 24 }}>
              CampsEZ attracts a highly valuable demographic — parents who invest in their children's development and actively seek quality products.
            </p>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {insights.map((insight, i) => (
                <motion.li
                  key={i}
                  initial={{ opacity: 0, x: -15 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
                >
                  <div style={{
                    width: 20, height: 20, background: BRAND, borderRadius: '50%', flexShrink: 0, marginTop: 2,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <CheckIcon size={11} style={{ color: '#fff' }} />
                  </div>
                  <span style={{ color: '#334155', fontSize: '0.875rem' }}>{insight}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Right: demographics chart */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
          >
            <h3 style={{ fontSize: '1.0625rem', fontWeight: 700, color: SLATE_900, marginBottom: 20 }}>
              Audience Demographics
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              {demographics.map(item => (
                <div key={item.label}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ color: '#334155', fontSize: '0.875rem', fontWeight: 500 }}>{item.label}</span>
                    <span style={{ color: SLATE_900, fontSize: '0.875rem', fontWeight: 700 }}>{item.value}%</span>
                  </div>
                  <div style={{ width: '100%', background: '#e2e8f0', borderRadius: 9999, height: 10 }}>
                    <div style={{ width: `${item.value}%`, background: BRAND, borderRadius: 9999, height: 10 }} />
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials ─── */
const testimonials = [
  {
    quote: 'We saw a huge increase in website traffic from leveraging CampsEZ — parents are exactly the audience we need — engaged and loyal. CampsEZ has built a very good community.',
    name: 'Sarah Mitchell', role: 'Marketing Director', company: 'ActiveKids Soccer Equipment',
  },
  {
    quote: 'The ROI has been great for us, our sponsored listings consistently drive qualified leads, and the newsletter features have become one of our highest-converting channels.',
    name: 'James Chen', role: 'Head of Growth', company: 'AdventuretheOutdoors Co.',
  },
  {
    quote: 'CampsEZ helped us reach parents in multiple cities without the complexity of managing multiple ad platforms. The targeting is precise and support is fantastic.',
    name: 'Emily Rodriguez', role: 'Founder & CEO', company: 'BrightMinds Educational Toys',
  },
];

function Testimonials() {
  return (
    <section style={{ padding: '56px 0', background: '#fff' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px' }}>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 40 }}
        >
          <h2 style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700, color: SLATE_900, marginBottom: 12 }}>
            Trusted by Leading Brands
          </h2>
          <p style={{ color: '#64748b', fontSize: '0.9375rem', maxWidth: 560, margin: '0 auto' }}>
            See what our advertising partners have to say
          </p>
        </motion.div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{ background: '#f8fafc', borderRadius: 12, padding: 24 }}
            >
              <QuoteIcon size={32} style={{ color: BRAND, marginBottom: 12 }} />
              <p style={{ color: '#475569', fontSize: '0.875rem', lineHeight: 1.7, fontStyle: 'italic', marginBottom: 20 }}>
                "{t.quote}"
              </p>
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: 12 }}>
                <div style={{ fontWeight: 700, color: SLATE_900, fontSize: '0.875rem' }}>{t.name}</div>
                <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: 2 }}>{t.role}</div>
                <div style={{ color: BRAND, fontSize: '0.75rem', fontWeight: 600, marginTop: 2 }}>{t.company}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Final CTA ─── */
function FinalCTA({ onOpen }) {
  return (
    <section style={{ padding: '64px 0', background: SLATE_900 }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 24px', textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', fontWeight: 700, color: '#fff', marginBottom: 16 }}>
            Ready to Reach Thousands of Active Families?
          </h2>
          <p style={{ color: '#cbd5e1', fontSize: '1rem', lineHeight: 1.7, marginBottom: 32, maxWidth: 580, margin: '0 auto 32px' }}>
            Join the brands already connecting with engaged parents on CampsEZ. Start advertising today and see results within days.
          </p>
          <button
            onClick={onOpen}
            style={{
              background: BRAND, color: '#fff', border: 'none', borderRadius: 8,
              padding: '14px 32px', fontWeight: 600, fontSize: '0.9375rem', cursor: 'pointer',
            }}
            onMouseEnter={e => e.currentTarget.style.background = BRAND_DARK}
            onMouseLeave={e => e.currentTarget.style.background = BRAND}
          >
            Get Started Today
          </button>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Page ─── */
const AdvertisingNew = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  return (
    <div>
      <HeroSection onOpen={openDialog} />
      <StatsBar />
      <WhyAdvertise />
      <AdProducts onOpen={openDialog} />
      <HowItWorks />
      <AudienceInsights />
      <Testimonials />
      <FinalCTA onOpen={openDialog} />
      <AdDialog open={dialogOpen} onClose={closeDialog} />
    </div>
  );
};

export default AdvertisingNew;
